import { useState } from "react";
import { styles } from "../../../utils/styles/products-trucks-styles";
import { useI18n } from "@/i18n/I18nProvider";
import Box from "@mui/material/Box";
import dynamic from "next/dynamic";
const PageHead = dynamic(() => import("@/components/PageHead"));
import ProductMain from "@/components/products-revamp/product-main";
import DynamicFAQSection from "@/components/DynamicFAQSection";
import CalcPopup from "@/components/products-revamp/CalcPopup";
import ApplyProduct from "@/components/products-revamp/product-apply";
import StepsRow from "@/components/steps-row";
import { colors } from "@/utils/const";
import PgWithImgSection from "@/components/products-revamp/pg-with-img-section";
import {
  getLocalizedValue,
  getStaticTextByKey,
} from "@/utils/general-functions";
import {
  emptyProps,
  getInstallmentsPlans,
  getProductPageData,
} from "@/services/get-product-data";
import Features from "@/components/products-revamp/features-section";
import { useSplitContentSections } from "@/hooks/product";
import SplitPgSection from "@/components/products-revamp/split-pg-section";
import { useProductLoanPackages } from "@/hooks/useProductLoanPackages";
import { getProductsText } from "../../../../locales/products";

export default function Index({
  heroData,
  contentWithImageData,
  faqData,
  stepsData,
  installmentsPlans,
  staticTextData,
  featuresData,
  mainData,
}) {
  const { t, locale } = useI18n();
  const products = getProductsText(locale);
  const staticTexts = getStaticTextByKey(staticTextData?.data);

  const { fullNavySections, splitSections } =
    useSplitContentSections(contentWithImageData);

  const plans = installmentsPlans?.[0]?.plans;

  const individualPlans = plans?.filter(
    (plan) => plan.section === "individual_plans",
  );

  const businessPlans = plans?.filter(
    (plan) => plan.section === "business_plans",
  );
  const { targetProduct, data } = useProductLoanPackages(products.trucks.id);
  const [openCalc, setOpenCalc] = useState(false);

  return (
    <>
      <PageHead
        title={getLocalizedValue(mainData[0], "meta_title", locale)}
        description={getLocalizedValue(mainData[0], "meta_description", locale)}
        scripts={getLocalizedValue(mainData[0], "schema", locale)}
      />

      <Box sx={{ ...styles.container, overflow: "hidden" }}>
        <ProductMain
          heroData={heroData}
          staticTexts={staticTexts}
          onApplyClick={() => {
            const formSection = document.getElementById("apply-form");
            if (formSection) {
              formSection.scrollIntoView({ behavior: "smooth" });
            }
          }}
          onCalcClick={() => setOpenCalc((prev) => !prev)}
          onExploreMoreClick={() => {
            const formSection = document.getElementById("trucks-features");
            if (formSection) {
              formSection.scrollIntoView({ behavior: "smooth" });
            }
          }}
          imgStyle={{
            mt:
              locale === "ar"
                ? { xs: 2, md: 8, xl: 7 }
                : { xs: 5, md: 7, xl: 6 },
          }}
          headerH1={true}
          shadow={true}
          shadowBottom={-60}
        />

        {fullNavySections?.length > 0 &&
          fullNavySections.map((section, index) => (
            <PgWithImgSection
              key={section.id || index}
              img={section.img}
              alt={section.alt_img}
              title={section.title?.text?.[locale]}
              titleType={section.title?.type}
              paragraph={section.description?.text?.[locale]}
              paragraphType={section.description?.type}
              imgReverse={index === 0 || index === 2 ? false : true}
              // isPreLine={true}
            />
          ))}
        {splitSections?.length > 0 &&
          splitSections.map((section, index) => (
            <SplitPgSection
              key={section.id || index}
              img={section.img}
              alt={section.alt_img}
              title={section.title?.text?.[locale]}
              titleType={section.title?.type}
              paragraph={section.description?.text?.[locale]}
              paragraphType={section.description?.type}
              bgHeight={{
                xs: "100%",
                md: locale === "ar" ? "50%" : "54%",
              }}
            />
          ))}
        <Box
          sx={{
            width: "100%",
            background: colors.navy,
            position: "relative",
          }}
        ></Box>

        {featuresData?.length > 0 &&
          featuresData.map((section, index) => (
            <Features
              key={section?.id || index}
              section={section}
              id="trucks-features"
            />
          ))}

        {/**faq section */}
        {faqData?.length > 0 &&
          faqData.map((section, index) => (
            <DynamicFAQSection
              key={section?.id || index}
              title={staticTexts?.faqs_section_title?.value?.text[locale] || ""}
              items={section?.faqs}
              renderHTML={true}
              variantTitle="h2"
              variantQ="h3"
            />
          ))}

        {/**Steps section */}
        {stepsData?.length > 0 &&
          stepsData.map((section, index) => (
            <StepsRow
              key={section.id || index}
              title={section?.title?.text?.[locale]}
              titleType={section?.title?.type}
              subtitle={section?.description?.text?.[locale]}
              subtitleType={section?.description?.type}
              steps={section?.items}
            />
          ))}

        <ApplyProduct
          img={heroData[0]?.img}
          title={mainData[0]?.form_title.text[locale]}
          titleType={mainData[0]?.form_title?.type}
          description={mainData[0]?.form_desc.text[locale]}
          descType={mainData[0]?.form_desc?.type}
          formType="a"
          product="trucks"
          source={41}
          shadow={true}
          shadowBottom={-60}
        />
        {targetProduct?.hasPlan && (
          <CalcPopup
            open={openCalc}
            onClose={() => setOpenCalc(false)}
            data={data}
            max_amount={targetProduct?.maxLimit}
            price_label={products.trucks.price}
          />
        )}
      </Box>
    </>
  );
}

export async function getServerSideProps() {
  try {
    const data = await getProductPageData("trucks");

    const installmentsPlans = await getInstallmentsPlans("trucks");

    return {
      props: {
        ...data,
        installmentsPlans: installmentsPlans?.installmentsPlans || [],
      },
    };
  } catch {
    return {
      props: {
        ...emptyProps,
        installmentsPlans: [],
      },
    };
  }
}
